import { PlanningModel } from "@/app/models/planning/planning.model";
import axios from "@/plugins/axios";
import {
  CheckinModel,
  CompleteCheckinRequest,
  CreateCheckinRequest,
  UpdateCheckinRequest,
} from "@/app/models/checkin/checkin.model";
import {
  DynamicForm,
  DynamicFormEntry, IndexList,
} from "@/app/dynamic-components/forms/dynamic-form.model";
import { convertTypeToApiType } from "@/app/helpers/api.helper";
import { Overview } from "@/app/models/overview/overview.model";
import store from "@/store";

export class CheckinService {
  async getCheckinByReferenceId(referenceId: string): Promise<CheckinModel> {
    const url = `${store.state.appConfig.apiBaseUrl}api/v2/checkin?page=0&pageSize=100&sort=&filter=nrq_referenceid:'${referenceId}'`;
    return axios.get<Overview>(url).then((response) => {
      if (response?.data?._embedded?.results) {
        response.data._embedded.results = Array.from(
          response.data._embedded.results
        ).map((value) => this.convertGetCheckinToCheckin(value));
      }
      return response.data._embedded.results.length > 0
        ? response.data._embedded.results[0]
        : null;
    });
  }
  async getCheckin(checkinId: string): Promise<CheckinModel> {
    const url = `${store.state.appConfig.apiBaseUrl}api/v2/checkin/${checkinId}`;
    return axios.get(url).then((response) => {
      return this.convertGetCheckinToCheckin(response.data);
    });
  }

  async createCheckinBulk(form: DynamicForm, create: Array<any>): Promise<string[]> {
    const url = `${store.state.appConfig.apiBaseUrl}api/v2/checkinbulk`;

    const requests: any[] = [];
    for (const value of create) {
      const createRequest = await this.convertFormDataToCheckinCreateRequest(form, value);
      createRequest.regardingType = convertTypeToApiType(createRequest.regardingType);
      requests.push(createRequest);
    }

    return axios.post(url, requests).then((response) => {
      return response.data;
    });
  }

  async createCheckin(form: DynamicForm, create: any): Promise<string> {
    const url = `${store.state.appConfig.apiBaseUrl}api/v2/checkin`;
    const createRequest = await this.convertFormDataToCheckinCreateRequest(
      form,
      create
    );
    createRequest.regardingType = convertTypeToApiType(
      createRequest.regardingType
    );
    return axios.post(url, createRequest).then((response) => {
      return response.data;
    });
  }

  async updateCheckin(
    form: DynamicForm,
    checkinId: string,
    update: any,
    cancelCheckin: boolean
  ): Promise<any> {
    const url = `${store.state.appConfig.apiBaseUrl}api/v2/checkin/${checkinId}`;
    const updateRequest = await this.convertFormDataToCheckinUpdateRequest(
      form,
      update
    );
    updateRequest.regardingType = convertTypeToApiType(
        updateRequest.regardingType
    );
    if (cancelCheckin) {
      updateRequest.status = "Canceled";
    } else {
      updateRequest.status = "Created";
    }
    return axios.patch(url, updateRequest).then((response) => {
      return response.data;
    });
  }

  async completeCheckin(
    form: DynamicForm,
    checkinId: string,
    update: any
  ): Promise<any> {
    const url = `${store.state.appConfig.apiBaseUrl}api/v2/checkin/${checkinId}`;
    const updateRequest = await this.convertFormDataToPlanningCompleteRequest(
      form,
      update
    );
    updateRequest.regardingType = convertTypeToApiType(
        updateRequest.regardingType
    );
    return axios.put(url, updateRequest).then((response) => {
      return response.data;
    });
  }

  private convertGetCheckinToCheckin(apiResponse: any): CheckinModel {
    apiResponse = { ...apiResponse, ...apiResponse.dynamic_Properties };
    apiResponse.status = apiResponse?.status?.toUpperCase();
    return apiResponse;
  }

  private async convertFormDataToCheckinCreateRequest(
    form: DynamicForm,
    formdata: any
  ): Promise<CreateCheckinRequest> {
    const mappedValues = DynamicFormEntry.SEPERATE_FIELDS(formdata, form);
    const result = mappedValues.data;
    result.dynamic_Properties = mappedValues.dynamicProperties;
    result.status = "CREATED";

    const indexers = new IndexList();
    await form.resolveIndexers(indexers);
    result['indexers'] = indexers;

    return result;
  }

  private async convertFormDataToCheckinUpdateRequest(
    form: DynamicForm,
    formdata: any
  ): Promise<UpdateCheckinRequest> {
    const mappedValues = DynamicFormEntry.SEPERATE_FIELDS(formdata, form);
    const result = mappedValues.data;
    result.dynamic_Properties = mappedValues.dynamicProperties;

    const indexers = new IndexList();
    await form.resolveIndexers(indexers);
    result['indexers'] = indexers;

    return result;
  }

  private async convertFormDataToPlanningCompleteRequest(
    form: DynamicForm,
    formdata: any
  ): Promise<CompleteCheckinRequest> {
    const mappedValues = DynamicFormEntry.SEPERATE_FIELDS(formdata, form);
    const result = mappedValues.data;
    result.dynamic_Properties = mappedValues.dynamicProperties;
    result.status = "Completed";

    const indexers = new IndexList();
    await form.resolveIndexers(indexers);
    result['indexers'] = indexers;

    return result;
  }
}
export const checkinService = new CheckinService();
